function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    // Bail before mutating the live DOM if newContent is missing.
    // Callers parse fetched HTML and querySelector for the new root,
    // which can return null when a cached or fetched response was a
    // password page, throttle soft-page, login redirect, or generic
    // error template (i.e. anything that passed product-info.js's
    // cache-write validation but didn't actually contain the expected
    // element). Without this guard we'd hide oldNode via display:none
    // while setInnerHTML(null.outerHTML) throws, leaving the section
    // blank until reload.
    if (!newContent) {
      console.warn('HTMLUpdateUtility.viewTransition: newContent missing, aborting swap');
      return;
    }

    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  // No role="button" here: ARIA in HTML allows no role on a <summary> that is a
  // direct child of <details>, and browsers already expose it as a disclosure
  // control. Stock Dawn set it and it fails WCAG 4.1.2 (IBM aria_role_valid).
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

const VARIANT_OPTION_PREFERENCE_KEY = 'variant:last-selected-options';

function readVariantOptionPreferences() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(VARIANT_OPTION_PREFERENCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
}

function writeVariantOptionPreferences(preferences) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (!preferences || typeof preferences !== 'object') return;
    window.localStorage.setItem(VARIANT_OPTION_PREFERENCE_KEY, JSON.stringify(preferences));
  } catch (error) {
    // Ignore environments where storage is not available
  }
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not([button-wishlist]):not(.quick-add__submit):not([data-quick-add-target]):not(.staff-picks__nav)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPage = 1;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.progressBar = this.querySelector('[data-slider-progress]');
    this.progressFill = this.querySelector('[data-slider-progress-fill]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    // Store ResizeObserver reference for cleanup in disconnectedCallback
    this.resizeObserver = new ResizeObserver((entries) => this.initPages());
    this.resizeObserver.observe(this.slider);

    // Performance: Throttle scroll handler to reduce high-frequency updates
    this.throttledUpdate = throttle(this.update.bind(this), 50);
    this.slider.addEventListener('scroll', this.throttledUpdate);
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  // Memory leak prevention: Clean up ResizeObserver when element is removed from DOM
  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.updateProgress(1, 1);
      return;
    }
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.updateProgress(1, this.totalPages);
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    this.updateProgress(this.currentPage, this.totalPages);

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  updateProgress(currentPage, totalPages) {
    if (!this.progressBar || !this.progressFill) return;

    if (!totalPages || totalPages <= 1) {
      this.progressBar.hidden = true;
      return;
    }

    this.progressBar.hidden = false;
    const segment = 100 / totalPages;
    const clampedPage = Math.min(Math.max(currentPage || 1, 1), totalPages);
    this.progressFill.style.width = `${segment}%`;
    this.progressFill.style.transform = `translateX(${(clampedPage - 1) * 100}%)`;
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  // Memory leak prevention: Clear autoplay interval and timeouts when element is removed
  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.autoplay) {
      clearInterval(this.autoplay);
      this.autoplay = null;
    }
    if (this.setPositionTimeout) {
      clearTimeout(this.setPositionTimeout);
      this.setPositionTimeout = null;
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.isApplyingStoredOptions = false;
    this.variantData = undefined;
    this.variantLookup = undefined;
    this.variantNameValueLookup = undefined;
    this.optionNameOrder = undefined;
    this.hasBroadcastedInitialSelection = false;
  }

  connectedCallback() {
    this.cacheVariantData();
    this.addEventListener('change', this.handleVariantChange);
    this.applyStoredSelections();
    this.refreshOptionAvailability();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange);
  }

  handleVariantChange(event) {
    const target = this.getInputForEventTarget(event.target);
    this.updateSelectionMetadata(event);

    if (!this.isApplyingStoredOptions) {
      this.persistSelectedOptions();
    }

    const selectedOptionValues = this.selectedOptionValues;
    const selectedOptionMap = this.collectSelectedOptions();
    const resolvedVariant = this.resolveVariant(selectedOptionValues, selectedOptionMap);
    if (resolvedVariant) {
      this.updateSelectedVariantScript(resolvedVariant);
    }

    // Re-mark the values that can't combine with what is now selected. Runs on
    // every change (stored-option application included) and needs no network,
    // so the picker tells the truth the instant the customer taps.
    this.refreshOptionAvailability();

    if (this.isApplyingStoredOptions) return;

    this.hasBroadcastedInitialSelection = true;
    // Record the variant being broadcast so a later swapped-in <variant-selects>
    // can recognize a redundant auto-broadcast of the same variant and break the
    // render -> replace -> re-broadcast loop (see broadcastCurrentSelection).
    // Lives on window so it survives the element swaps that drive the loop.
    window.__lastBroadcastVariantId = resolvedVariant?.id ?? null;
    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target,
        selectedOptionValues,
        selectedOptionMap,
        selectedVariantId: resolvedVariant?.id,
        selectedVariant: resolvedVariant || null,
      },
    });
  }

  getOptionGroups() {
    return Array.from(this.querySelectorAll('[data-option-name]'))
      .map((element) => {
        const optionName = element.dataset.optionName;
        const tagName = element.tagName;
        if (!optionName || (tagName !== 'SELECT' && tagName !== 'FIELDSET')) return null;
        return { optionName, element };
      })
      .filter(Boolean);
  }

  getOptionValue(element) {
    if (!element) return null;
    if (element.tagName === 'SELECT') {
      return element.value || null;
    }
    if (element.tagName === 'FIELDSET') {
      const checkedInput = element.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    }
    return null;
  }

  collectSelectedOptions() {
    return this.getOptionGroups().reduce((acc, { optionName, element }) => {
      const value = this.getOptionValue(element);
      if (value) {
        acc[optionName] = value;
      }
      return acc;
    }, {});
  }

  persistSelectedOptions() {
    const selections = this.collectSelectedOptions();
    if (!Object.keys(selections).length) return;
    writeVariantOptionPreferences(selections);
  }

  applyStoredSelections() {
    this.cacheVariantData();
    const rawStoredSelections = readVariantOptionPreferences();
    if (!rawStoredSelections) return;

    // Never apply a stored combination this product does not actually make.
    const storedSelections = this.usableStoredSelections(rawStoredSelections);
    if (!storedSelections) return;

    const groups = this.getOptionGroups();
    let applied = false;
    let lastChangedElement = null;

    groups.forEach(({ optionName, element }) => {
      const desiredValue = storedSelections[optionName];
      if (!desiredValue) return;

      if (element.tagName === 'SELECT') {
        if (element.value === desiredValue) return;
        const matchingOption = Array.from(element.options || []).find(
          (option) => option.value === desiredValue
        );
        if (!matchingOption) return;
        if (!applied) {
          this.isApplyingStoredOptions = true;
          applied = true;
        }
        const wasDisabled = matchingOption.disabled;
        if (wasDisabled) matchingOption.disabled = false;
        element.value = desiredValue;
        element.dispatchEvent(new Event('change', { bubbles: true }));
        lastChangedElement = element;
      } else {
        const inputs = Array.from(element.querySelectorAll('input'));
        const matchingInput = inputs.find(
          (input) => input.value === desiredValue || input.dataset.optionValueId === desiredValue
        );
        if (!matchingInput || matchingInput.checked) return;
        if (!applied) {
          this.isApplyingStoredOptions = true;
          applied = true;
        }
        const wasDisabled = matchingInput.disabled;
        const hadDisabledClass = matchingInput.classList.contains('disabled');
        const hadVisuallyDisabledClass = matchingInput.classList.contains('visually-disabled');
        if (wasDisabled) matchingInput.disabled = false;
        if (hadDisabledClass) matchingInput.classList.remove('disabled');
        if (hadVisuallyDisabledClass) matchingInput.classList.remove('visually-disabled');
        const skipScrollFocus =
          typeof window !== 'undefined' && window.matchMedia?.('(hover: none) and (pointer: coarse)')?.matches;
        if (!skipScrollFocus && typeof matchingInput.focus === 'function') {
          try {
            matchingInput.focus({ preventScroll: true });
          } catch (error) {
            matchingInput.focus();
          }
        }
        matchingInput.click();
        lastChangedElement = matchingInput;
      }
    });

    if (applied) {
      setTimeout(() => {
        this.isApplyingStoredOptions = false;
        this.broadcastCurrentSelection(lastChangedElement);
      });
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  cacheVariantData() {
    if (this.variantData !== undefined) return;

    const script = this.querySelector('[data-product-variants]');
    if (!script) {
      this.variantData = [];
      this.variantLookup = new Map();
      this.variantNameValueLookup = new Map();
      return;
    }

    try {
      this.variantData = JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.error('Failed to parse product variants data', error);
      this.variantData = [];
    }

    this.optionNameOrder = this.getOptionGroups().map(({ optionName }) => optionName);

    this.variantLookup = new Map(
      (this.variantData || []).reduce((accumulator, variant) => {
        const optionValueIds = Array.isArray(variant?.option_value_ids)
          ? variant.option_value_ids.map((value) => String(value))
          : [];
        if (!optionValueIds.length || variant?.id === undefined) return accumulator;
        accumulator.push([optionValueIds.join(','), variant]);
        return accumulator;
      }, [])
    );

    this.variantNameValueLookup = new Map(
      (this.variantData || []).reduce((accumulator, variant) => {
        const options = Array.isArray(variant?.options) ? variant.options : [];
        const key = this.buildNameValueKey(options);
        if (!key) return accumulator;
        accumulator.push([key, variant]);
        return accumulator;
      }, [])
    );
  }

  resolveVariant(optionValueIds = [], optionValueMap = {}) {
    const variantByIds = this.findVariantByIds(optionValueIds);
    if (variantByIds) return variantByIds;

    return this.findVariantByNames(optionValueMap);
  }

  findVariantByIds(optionValueIds = []) {
    if (!optionValueIds?.length || !this.variantLookup) return null;
    const key = optionValueIds.filter(Boolean).map((value) => String(value)).join(',');
    if (!key) return null;
    return this.variantLookup.get(key) || null;
  }

  findVariantByNames(optionValueMap = {}) {
    if (!optionValueMap || typeof optionValueMap !== 'object' || !this.variantNameValueLookup) return null;
    if (!this.optionNameOrder?.length) return null;

    const orderedPairs = this.optionNameOrder
      .map((name) => ({
        name,
        value: optionValueMap[name],
      }))
      .filter(({ value }) => value !== undefined && value !== null && value !== '');

    if (!orderedPairs.length) return null;

    const key = this.buildNameValueKey(orderedPairs);
    if (!key) return null;

    return this.variantNameValueLookup.get(key) || null;
  }

  buildNameValueKey(optionPairs = []) {
    if (!Array.isArray(optionPairs)) return '';

    return optionPairs
      .map(({ name, value }) => {
        if (typeof name === 'undefined' || typeof value === 'undefined' || value === null) return '';
        const normalizedName = String(name).trim().toLowerCase();
        const normalizedValue = String(value).trim().toLowerCase();
        if (!normalizedName || !normalizedValue) return '';
        return `${normalizedName}::${normalizedValue}`;
      })
      .filter(Boolean)
      .join('||');
  }

  // True when at least one variant carries every {optionName: value} pair given.
  hasVariantMatching(selections) {
    const entries = Object.entries(selections || {});
    if (!entries.length) return false;

    return (this.variantData || []).some((variant) => {
      const options = Array.isArray(variant?.options) ? variant.options : [];
      return entries.every(([name, value]) => {
        const option = options.find((candidate) => candidate.name === name);
        return !!option && String(option.value) === String(value);
      });
    });
  }

  // The largest prefix of the stored size preference that resolves to a real
  // variant on THIS product, or null.
  //
  // The stored options are whatever the customer last picked on WHATEVER
  // product. Applying them group-by-group — which is what this used to do — is
  // what left the PDP dead on "Unavailable" before the customer touched
  // anything: 39% of JNCO's two-option products have holes in the size grid
  // (a 26" waist is made in a 30 and 32 inseam but never a 34), so a stored
  // 26 x 34 checked both pills, resolved to no variant, and disabled the
  // button. Worse, it stuck: localStorage kept the dead pair, so every later
  // product AND every AJAX colour switch opened the same way until the
  // customer found a valid pair by hand. Reported by the client 2026-08-18.
  //
  // Options are dropped from the LAST group backwards, so Waist — the primary
  // decision — survives and Inseam gives way. Whatever is dropped is left
  // unselected, which lands on the recoverable "Select size" prompt; we never
  // silently substitute a size the customer did not ask for.
  usableStoredSelections(storedSelections) {
    if (!storedSelections || typeof storedSelections !== 'object') return null;

    // Only the option groups this product actually renders, in option order.
    const names = this.getOptionGroups()
      .map(({ optionName }) => optionName)
      .filter((name) => storedSelections[name] !== undefined && storedSelections[name] !== null);
    if (!names.length) return null;

    for (let length = names.length; length > 0; length -= 1) {
      const candidate = {};
      names.slice(0, length).forEach((name) => {
        candidate[name] = storedSelections[name];
      });
      if (this.hasVariantMatching(candidate)) return candidate;
    }

    return null;
  }

  // Grey out the values that cannot combine with the current selection.
  //
  // Shopify's own `product_option_value.available` does NOT cover this: it
  // flags a value only when the variant exists and is out of stock. For a pair
  // that was never built — 26 x 34 above — every value still renders as
  // available, so the customer taps a normal-looking pill and dead-ends on
  // "Unavailable" with nothing on screen explaining why. Verified against the
  // Section Rendering API 2026-08-18: rendering ?variant=<26/28> marks nothing
  // disabled even though 26 has no 34.
  //
  // Computed from the variant JSON already on the page, so it costs no request
  // and lands in the same frame as the tap. Only marks the theme's existing
  // `.disabled` state, which also reveals the visually-hidden "unavailable"
  // text for screen readers (see component-product-variant-picker.css). The
  // pills stay clickable and focusable — same as a sold-out value — so a
  // customer can still select one and read the resulting button state.
  //
  // Marks made here are tagged with data-combo-unavailable and only those are
  // ever cleared, so the server-rendered sold-out marking is left intact.
  refreshOptionAvailability() {
    this.cacheVariantData();

    const variants = this.variantData;
    if (!Array.isArray(variants) || !variants.length) return;

    const groups = this.getOptionGroups();
    if (groups.length < 2) return; // nothing to constrain against

    const selected = this.collectSelectedOptions();

    groups.forEach(({ optionName, element }) => {
      const inputs = Array.from(element.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return; // dropdown pickers keep the server's state

      const possible = new Set();
      variants.forEach((variant) => {
        const options = Array.isArray(variant?.options) ? variant.options : [];
        const own = options.find((option) => option.name === optionName);
        if (!own) return;
        const fitsOtherGroups = options.every((option) => {
          if (option.name === optionName) return true;
          const chosen = selected[option.name];
          return !chosen || String(option.value) === String(chosen);
        });
        if (fitsOtherGroups) possible.add(String(own.value));
      });

      // A group with nothing possible means the variant JSON and the rendered
      // picker disagree; leave the server's state alone rather than blanking
      // out every value.
      if (!possible.size) return;

      inputs.forEach((input) => {
        if (!possible.has(String(input.value))) {
          input.dataset.comboUnavailable = 'true';
          input.classList.add('disabled');
        } else if (input.dataset.comboUnavailable) {
          delete input.dataset.comboUnavailable;
          input.classList.remove('disabled');
        }
      });
    });
  }

  updateSelectedVariantScript(variant) {
    const script = this.querySelector('[data-selected-variant]');
    if (!script) return;
    try {
      script.textContent = JSON.stringify(variant);
    } catch (error) {
      console.error('Failed to serialize selected variant', error);
    }
  }

  broadcastCurrentSelection(preferredTarget = null) {
    if (this.hasBroadcastedInitialSelection) return;

    const selectedOptionValues = this.selectedOptionValues;
    if (!selectedOptionValues.length) return;

    // Loop breaker. An AJAX-swapped-in <variant-selects> auto-broadcasts its
    // selection on connect (applyStoredSelections -> here). product-info handles
    // that by re-rendering, which can replace this element with yet another
    // instance that auto-broadcasts again. When the resolved variant is
    // unchanged, that cascade is unbounded — it froze iOS Safari, each turn also
    // firing a failing /variants/<id>.js request. If the variant we'd broadcast
    // is the one already broadcast, product-info already reflects it: mark as
    // broadcast and stop. Genuine user changes publish through handleVariantChange
    // directly (not this path) and are unaffected, since each picks a different
    // variant id.
    const resolvedVariant = this.resolveVariant(selectedOptionValues, this.collectSelectedOptions());
    const resolvedId = resolvedVariant?.id ?? null;
    if (resolvedId != null && window.__lastBroadcastVariantId === resolvedId) {
      this.hasBroadcastedInitialSelection = true;
      return;
    }

    let target = preferredTarget;
    if (!target) {
      const groups = this.getOptionGroups();
      for (const { element } of groups) {
        if (element.tagName === 'SELECT') {
          target = element;
          break;
        }

        const checkedInput = element.querySelector('input:checked');
        if (checkedInput) {
          target = checkedInput;
          break;
        }
      }
    }

    if (!target) return;
    this.handleVariantChange({ target });
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class DynamicCollectionTabs extends HTMLElement {
  constructor() {
    super();
    this.parentTabs = this.querySelectorAll('.parent-tab');
    this.collectionGroups = this.querySelectorAll('.collection-group');
    const rootAttr = this.dataset.rootUrl || '/';
    this.rootUrl = rootAttr.endsWith('/') ? rootAttr : `${rootAttr}/`;
    this.requestCounter = 0;
    this.currentRequestId = 0;
  }

  connectedCallback() {
    this.setupParentTabs();
    this.setupCollectionTabs();

    const firstParent = this.parentTabs[0];
    const autoLoadFirst = this.dataset.autoloadFirst === 'true';
    if (autoLoadFirst && firstParent) {
      firstParent.click();
    }
  }

  setupParentTabs() {
    this.parentTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const parent = tab.dataset.parent;

        this.parentTabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        this.collectionGroups.forEach(group => {
          group.classList.add('hidden');
        });

        const group = this.querySelector(`.collection-group[data-parent="${parent}"]`);
        if (group) group.classList.remove('hidden');

        const shopAllLink = this.querySelector(`.collection-shop-all__link[data-parent="${parent}"]`);

        const firstCollectionTab = this.querySelector(`.collection-tab[data-parent="${parent}"]`);
        if (firstCollectionTab) {
          firstCollectionTab.click();
        } else {
          const container = this.querySelector(`#collection-products-${parent}`);
          if (container) {
            container.innerHTML = '<p>No collections available for this category.</p>';
          }
          if (shopAllLink) {
            shopAllLink.href = '#';
            shopAllLink.setAttribute('aria-disabled', 'true');
          }
        }
      });
    });
  }

  setupCollectionTabs() {
    this.addEventListener('click', (e) => {
      if (!e.target.classList.contains('collection-tab')) return;

      const tab = e.target;
      const parent = tab.dataset.parent;
      const handle = tab.dataset.collection;
      const container = this.querySelector(`#collection-products-${parent}`);
      const shopAllLink = this.querySelector(`.collection-shop-all__link[data-parent="${parent}"]`);

      if (!handle) {
        console.warn('Missing collection handle for tab click');
        if (container) {
          container.innerHTML = '<p>No collection selected for this tab.</p>';
        }
        if (shopAllLink) {
          shopAllLink.href = '#';
          shopAllLink.setAttribute('aria-disabled', 'true');
        }
        return;
      }

      this.querySelectorAll(`.collection-tab[data-parent="${parent}"]`).forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      if (shopAllLink) {
        shopAllLink.href = `${this.rootUrl}collections/${encodeURIComponent(handle)}`;
        shopAllLink.removeAttribute('aria-disabled');
      }

      if (container) {
        container.classList.add('is-loading');
        container.setAttribute('aria-busy', 'true');
        const oldGrid = container.querySelector('.product-grid');
        if (oldGrid) {
          oldGrid.style.visibility = 'hidden';
        }
        const prevHeight = container.offsetHeight;
        container.style.height = prevHeight + 'px';
      }

      const url = `${this.rootUrl}collections/${encodeURIComponent(handle)}?view=product-grid`;
      console.debug('Fetching collection:', url);
      const requestId = ++this.requestCounter;
      this.currentRequestId = requestId;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(res.statusText);
          return res.text();
        })
        .then(html => {
          if (requestId !== this.currentRequestId) return; // stale response, ignore
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const productGrid = doc.querySelector('.product-grid');
          if (!container) return;

          if (!productGrid) {
            container.classList.remove('is-loading');
            container.removeAttribute('aria-busy');
            container.innerHTML = '<p>No products found</p>';
            return;
          }

          const newGrid = productGrid;
          // Always swap to the new grid hidden, then fade it in while keeping overlay
          newGrid.style.opacity = '0';
          newGrid.style.transition = 'opacity 200ms ease';
          container.innerHTML = '';
          container.appendChild(newGrid);
          requestAnimationFrame(() => {
            // Force reflow, then fade up
            void newGrid.offsetHeight;
            newGrid.style.opacity = '1';
            newGrid.addEventListener('transitionend', () => {
              container.classList.remove('is-loading');
              container.removeAttribute('aria-busy');
              container.style.height = '';
              newGrid.style.transition = '';
              newGrid.style.opacity = '';
            }, { once: true });
          });
        })
        .catch(error => {
          if (requestId !== this.currentRequestId) return; // stale error, ignore
          console.error('Error fetching collection products:', error);
          if (container) {
            container.innerHTML = '<p>There was an error loading products. Please try again.</p>';
            container.classList.remove('is-loading');
            container.removeAttribute('aria-busy');
          }
        });
    });
  }
}

customElements.define('dynamic-collection-tabs', DynamicCollectionTabs);

function initStaffPicksCarousels(root = document) {
  const scope = root instanceof Element ? root : document;
  const carousels = scope.querySelectorAll('[data-staff-picks-carousel]');
  if (!carousels.length) return;

  carousels.forEach((carousel) => {
    if (carousel.dataset.staffPicksInit === 'true') return;

    const track = carousel.querySelector('[data-staff-picks-track]');
    const prev = carousel.querySelector('[data-staff-picks-prev]');
    const next = carousel.querySelector('[data-staff-picks-next]');
    if (!track || !prev || !next) return;

    carousel.dataset.staffPicksInit = 'true';

    const getScrollAmount = () => {
      const firstCard = track.querySelector('.staff-picks__product');
      if (!firstCard) return track.clientWidth || 0;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0');
      return firstCard.getBoundingClientRect().width + gap;
    };

    const setButtonDisabled = (button, disabled) => {
      if (!button) return;
      button.classList.toggle('is-disabled', !!disabled);
      if (disabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    };

    const updateNavState = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const nearStart = track.scrollLeft <= 1;
      const nearEnd = track.scrollLeft >= maxScroll - 1;

      setButtonDisabled(prev, nearStart);
      setButtonDisabled(next, nearEnd);

      const hideControls = maxScroll <= 1;
      [prev, next].forEach((button) => {
        if (hideControls) button.setAttribute('hidden', 'hidden');
        else button.removeAttribute('hidden');
      });
    };

    const scrollByStep = (direction) => {
      const amount = getScrollAmount();
      if (amount <= 0) return;
      track.scrollBy({ left: amount * direction, behavior: 'smooth' });
    };

    prev.addEventListener('click', () => {
      if (prev.classList.contains('is-disabled')) return;
      scrollByStep(-1);
    });
    next.addEventListener('click', () => {
      if (next.classList.contains('is-disabled')) return;
      scrollByStep(1);
    });

    let rafId = null;
    const onTrackScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        updateNavState();
        rafId = null;
      });
    };
    track.addEventListener('scroll', onTrackScroll, { passive: true });

    const resizeHandler = () => updateNavState();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(resizeHandler);
      resizeObserver.observe(track);
      carousel._staffPicksResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', resizeHandler);
      carousel._staffPicksResizeFallback = resizeHandler;
    }

    updateNavState();
    // Ensure nav state updates after images load
    window.requestAnimationFrame(updateNavState);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initStaffPicksCarousels();
});

document.addEventListener('shopify:section:load', (event) => {
  initStaffPicksCarousels(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target
    .querySelectorAll('[data-staff-picks-carousel]')
    .forEach((carousel) => {
      if (carousel._staffPicksResizeObserver) {
        carousel._staffPicksResizeObserver.disconnect();
        delete carousel._staffPicksResizeObserver;
      }
      if (carousel._staffPicksResizeFallback) {
        window.removeEventListener('resize', carousel._staffPicksResizeFallback);
        delete carousel._staffPicksResizeFallback;
      }
      delete carousel.dataset.staffPicksInit;
    });
});

function syncColorGroupActiveState(root = document) {
  const scope = root instanceof Element ? root : document;
  const lists = scope.querySelectorAll('[data-color-product-list]');
  if (!lists.length) return;

  lists.forEach((list) => {
    const activeId = list.getAttribute('data-current-product-id');
    if (!activeId) return;

    list.querySelectorAll('[data-color-product-id]').forEach((item) => {
      const isActive = item.getAttribute('data-color-product-id') === activeId;
      item.classList.toggle('product__color-group-item--active', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'page');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  });
}

function initColorGroupCarousels(root = document) {
  const scope = root instanceof Element ? root : document;
  const containers = scope.querySelectorAll('[data-color-product-container]');
  if (!containers.length) return;

  containers.forEach((container) => {
    if (container.dataset.colorGroupCarouselInit === 'true') return;

    const list = container.querySelector('[data-color-product-list]');
    const prev = container.querySelector('[data-color-group-nav="prev"]');
    const next = container.querySelector('[data-color-group-nav="next"]');

    if (!list || !prev || !next) return;

    container.dataset.colorGroupCarouselInit = 'true';

    const prefersReducedMotion = () =>
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const updateState = () => {
      const maxScroll = Math.max(list.scrollWidth - list.clientWidth, 0);
      const hasOverflow = maxScroll > 1;

      container.classList.toggle('product__color-group-carousel--overflow', hasOverflow);

      const atStart = list.scrollLeft <= 4;
      const atEnd = list.scrollLeft >= maxScroll - 4;

      prev.disabled = !hasOverflow || atStart;
      next.disabled = !hasOverflow || atEnd;
    };

    const scrollByDirection = (direction) => {
      const firstItem = list.querySelector('.product__color-group-item');
      const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : 0;
      const scrollAmount = Math.max(list.clientWidth * 0.7, itemWidth * 2, 120);
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

      if (typeof list.scrollBy === 'function') {
        list.scrollBy({ left: scrollAmount * direction, behavior });
      } else {
        list.scrollLeft += scrollAmount * direction;
      }
    };

    prev.addEventListener('click', () => scrollByDirection(-1));
    next.addEventListener('click', () => scrollByDirection(1));

    list.addEventListener('scroll', updateState, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateState);
      resizeObserver.observe(list);
    } else {
      window.addEventListener('resize', updateState);
    }

    updateState();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  syncColorGroupActiveState();
  initColorGroupCarousels();
});

document.addEventListener('shopify:section:load', (event) => {
  syncColorGroupActiveState(event.target);
  initColorGroupCarousels(event.target);
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, () => {
    syncColorGroupActiveState();
    initColorGroupCarousels();
  });
}

function relocateKsChart(attempt = 0) {
  const MAX_ATTEMPTS = 15;
  const chart = document.querySelector('.ks-chart-container');
  const colorGroupList = document.querySelector('.product__color-group-list');
  const colorGroupContainer = document.querySelector('[data-color-product-container]');

  const anchorElement = colorGroupContainer || colorGroupList;

  if (!anchorElement || !chart) {
    if (attempt < MAX_ATTEMPTS) {
      window.setTimeout(() => relocateKsChart(attempt + 1), 200);
    }
    return;
  }

  const alreadyRelocated = chart.dataset.ksChartRelocated === 'true';
  const alreadyPositioned =
    chart.previousElementSibling === anchorElement ||
    anchorElement.nextElementSibling === chart ||
    (colorGroupContainer && colorGroupContainer.contains(chart)) ||
    (colorGroupList && colorGroupList.contains(chart));

  if (!alreadyRelocated && !alreadyPositioned) {
    anchorElement.insertAdjacentElement('afterend', chart);
    chart.dataset.ksChartRelocated = 'true';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  relocateKsChart();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event && event.target && event.target.querySelector && event.target.querySelector('.ks-chart-container')) {
    relocateKsChart();
  }
});

class FooterAccordion extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const toggles = this.querySelectorAll('.footer-toggle');

    toggles.forEach(toggle => {
      const content = toggle.nextElementSibling;
      console.log('content', content);

      toggle.setAttribute('aria-expanded', 'false');
      content.setAttribute('aria-hidden', 'true');

      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';

        // Collapse all first
        toggles.forEach(t => {
          t.setAttribute('aria-expanded', 'false');
          t.nextElementSibling.setAttribute('aria-hidden', 'true');
        });

        // Expand clicked one if it wasn't already open
        if (!expanded) {
          toggle.setAttribute('aria-expanded', 'true');
          content.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }
}

customElements.define('footer-accordion', FooterAccordion);


document.addEventListener('click', (event) => {
  const dismissBtn = event.target.closest('[id^="ModalDClose-"]');
  if (!dismissBtn) return;

  const suffix = dismissBtn.id.replace('ModalDClose-', '');
  const closeBtn = document.getElementById(`ModalClose-${suffix}`);
  if (closeBtn) closeBtn.click();
});


(() => {
  'use strict';

  // SVG icons once
  const ICONS = {
    play: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M16.292 26.542 26.542 20 16.292 13.458ZM20 35.833Q16.75 35.833 13.854 34.583Q10.958 33.333 8.812 31.188Q6.667 29.042 5.417 26.146Q4.167 23.25 4.167 20Q4.167 16.75 5.417 13.854Q6.667 10.958 8.812 8.813Q10.958 6.667 13.854 5.417Q16.75 4.167 20 4.167Q23.25 4.167 26.146 5.417Q29.042 6.667 31.187 8.813Q33.333 10.958 34.583 13.854Q35.833 16.75 35.833 20Q35.833 23.25 34.583 26.146Q33.333 29.042 31.187 31.188Q29.042 33.333 26.146 34.583Q23.25 35.833 20 35.833ZM20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20ZM20 33.75Q25.667 33.75 29.708 29.708Q33.75 25.667 33.75 20Q33.75 14.333 29.708 10.292Q25.667 6.25 20 6.25Q14.333 6.25 10.292 10.292Q6.25 14.333 6.25 20Q6.25 25.667 10.292 29.708Q14.333 33.75 20 33.75Z" fill="currentColor"/></svg>',
    pause: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M15.875 26.25H17.958V13.75H15.875ZM22.042 26.25H24.125V13.75H22.042ZM20 35.833Q16.75 35.833 13.854 34.583Q10.958 33.333 8.812 31.188Q6.667 29.042 5.417 26.146Q4.167 23.25 4.167 20Q4.167 16.75 5.417 13.854Q6.667 10.958 8.812 8.813Q10.958 6.667 13.854 5.417Q16.75 4.167 20 4.167Q23.25 4.167 26.146 5.417Q29.042 6.667 31.187 8.813Q33.333 10.958 34.583 13.854Q35.833 16.75 35.833 20Q35.833 23.25 34.583 26.146Q33.333 29.042 31.187 31.188Q29.042 33.333 26.146 34.583Q23.25 35.833 20 35.833ZM20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20ZM20 33.75Q25.667 33.75 29.708 29.708Q33.75 25.667 33.75 20Q33.75 14.333 29.708 10.292Q25.667 6.25 20 6.25Q14.333 6.25 10.292 10.292Q6.25 14.333 6.25 20Q6.25 25.667 10.292 29.708Q14.333 33.75 20 33.75Z" fill="currentColor"/></svg>',
    volume: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M23.417 33.5V31.333Q27.167 30.167 29.5 27.021Q31.833 23.875 31.833 19.958Q31.833 16.042 29.5 12.896Q27.167 9.75 23.417 8.583V6.417Q28.042 7.667 30.979 11.417Q33.917 15.167 33.917 19.958Q33.917 24.75 30.979 28.5Q28.042 32.25 23.417 33.5ZM6.083 24.167V15.833H12.292L19.417 8.667V31.333L12.292 24.167ZM23 26.25V13.667Q24.792 14.5 25.854 16.208Q26.917 17.917 26.917 20Q26.917 22.083 25.854 23.771Q24.792 25.458 23 26.25ZM17.333 13.875 13.208 17.917H8.167V22.083H13.208L17.333 26.125ZM13.292 20Z" fill="currentColor"/></svg>',
    muted: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M32.875 36.917 27.625 31.667Q26.708 32.292 25.646 32.75Q24.583 33.208 23.5 33.5V31.333Q24.208 31.125 24.875 30.854Q25.542 30.583 26.125 30.125L19.5 23.5V31.333L12.375 24.167H6.167V15.833H11.833L3.167 7.208L4.667 5.708L34.375 35.375ZM31.5 27.875 30 26.375Q30.958 24.958 31.438 23.333Q31.917 21.708 31.917 19.958Q31.917 16 29.604 12.833Q27.292 9.667 23.5 8.583V6.417Q28.167 7.667 31.083 11.417Q34 15.167 34 19.958Q34 22.125 33.354 24.125Q32.708 26.125 31.5 27.875ZM15.667 19.708ZM26.417 22.792 23.5 19.875V13.833Q25.167 14.75 26.083 16.417Q27 18.083 27 20Q27 20.75 26.854 21.438Q26.708 22.125 26.417 22.792ZM19.5 15.875 15.917 12.292 19.5 8.667ZM17.417 26.208V21.417L13.917 17.917H8.25V22.083H13.25Z" fill="currentColor"/></svg>'
  };

  (() => {
    'use strict';

    // SVG icons once
    const ICONS = {
      play: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M16.292 26.542 26.542 20 16.292 13.458ZM20 35.833Q16.75 35.833 13.854 34.583Q10.958 33.333 8.812 31.188Q6.667 29.042 5.417 26.146Q4.167 23.25 4.167 20Q4.167 16.75 5.417 13.854Q6.667 10.958 8.812 8.813Q10.958 6.667 13.854 5.417Q16.75 4.167 20 4.167Q23.25 4.167 26.146 5.417Q29.042 6.667 31.187 8.813Q33.333 10.958 34.583 13.854Q35.833 16.75 35.833 20Q35.833 23.25 34.583 26.146Q33.333 29.042 31.187 31.188Q29.042 33.333 26.146 34.583Q23.25 35.833 20 35.833ZM20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20ZM20 33.75Q25.667 33.75 29.708 29.708Q33.75 25.667 33.75 20Q33.75 14.333 29.708 10.292Q25.667 6.25 20 6.25Q14.333 6.25 10.292 10.292Q6.25 14.333 6.25 20Q6.25 25.667 10.292 29.708Q14.333 33.75 20 33.75Z" fill="currentColor"/></svg>',
      pause: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M15.875 26.25H17.958V13.75H15.875ZM22.042 26.25H24.125V13.75H22.042ZM20 35.833Q16.75 35.833 13.854 34.583Q10.958 33.333 8.812 31.188Q6.667 29.042 5.417 26.146Q4.167 23.25 4.167 20Q4.167 16.75 5.417 13.854Q6.667 10.958 8.812 8.813Q10.958 6.667 13.854 5.417Q16.75 4.167 20 4.167Q23.25 4.167 26.146 5.417Q29.042 6.667 31.187 8.813Q33.333 10.958 34.583 13.854Q35.833 16.75 35.833 20Q35.833 23.25 34.583 26.146Q33.333 29.042 31.187 31.188Q29.042 33.333 26.146 34.583Q23.25 35.833 20 35.833ZM20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20Q20 20 20 20ZM20 33.75Q25.667 33.75 29.708 29.708Q33.75 25.667 33.75 20Q33.75 14.333 29.708 10.292Q25.667 6.25 20 6.25Q14.333 6.25 10.292 10.292Q6.25 14.333 6.25 20Q6.25 25.667 10.292 29.708Q14.333 33.75 20 33.75Z" fill="currentColor"/></svg>',
      volume: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M23.417 33.5V31.333Q27.167 30.167 29.5 27.021Q31.833 23.875 31.833 19.958Q31.833 16.042 29.5 12.896Q27.167 9.75 23.417 8.583V6.417Q28.042 7.667 30.979 11.417Q33.917 15.167 33.917 19.958Q33.917 24.75 30.979 28.5Q28.042 32.25 23.417 33.5ZM6.083 24.167V15.833H12.292L19.417 8.667V31.333L12.292 24.167ZM23 26.25V13.667Q24.792 14.5 25.854 16.208Q26.917 17.917 26.917 20Q26.917 22.083 25.854 23.771Q24.792 25.458 23 26.25ZM17.333 13.875 13.208 17.917H8.167V22.083H13.208L17.333 26.125ZM13.292 20Z" fill="currentColor"/></svg>',
      muted: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" height="40" width="40"><path d="M32.875 36.917 27.625 31.667Q26.708 32.292 25.646 32.75Q24.583 33.208 23.5 33.5V31.333Q24.208 31.125 24.875 30.854Q25.542 30.583 26.125 30.125L19.5 23.5V31.333L12.375 24.167H6.167V15.833H11.833L3.167 7.208L4.667 5.708L34.375 35.375ZM31.5 27.875 30 26.375Q30.958 24.958 31.438 23.333Q31.917 21.708 31.917 19.958Q31.917 16 29.604 12.833Q27.292 9.667 23.5 8.583V6.417Q28.167 7.667 31.083 11.417Q34 15.167 34 19.958Q34 22.125 33.354 24.125Q32.708 26.125 31.5 27.875ZM15.667 19.708ZM26.417 22.792 23.5 19.875V13.833Q25.167 14.75 26.083 16.417Q27 18.083 27 20Q27 20.75 26.854 21.438Q26.708 22.125 26.417 22.792ZM19.5 15.875 15.917 12.292 19.5 8.667ZM17.417 26.208V21.417L13.917 17.917H8.25V22.083H13.25Z" fill="currentColor"/></svg>'
    };

    // ---- Web Component: <hosted-video> ----
    class HostedVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.playButton = this.querySelector('.video__controls--play');
        this.muteButton = this.querySelector('.video__controls--mute');
        if (!this.video) return;

        // initial UI
        this.#setPlayUI(!this.video.paused && !this.video.ended);
        this.#setMuteUI(this.video.muted);

        // control interactions
        this.playButton?.addEventListener('click', () => this.togglePlay());
        this.muteButton?.addEventListener('click', () => this.toggleMute());

        // keep UI in sync with *actual* media state (fixes opener case)
        this._onPlay = () => this.#setPlayUI(true);
        this._onPause = () => this.#setPlayUI(false);
        this._onEnded = () => this.#setPlayUI(false);
        this._onVolume = () => this.#setMuteUI(this.video.muted);

        this.video.addEventListener('play', this._onPlay);
        this.video.addEventListener('pause', this._onPause);
        this.video.addEventListener('ended', this._onEnded);
        this.video.addEventListener('volumechange', this._onVolume);

        // optional: pause siblings when this one plays
        this.video.addEventListener('play', () => {
          if (!this.hasAttribute('data-autopause')) return;
          document.querySelectorAll('hosted-video').forEach((el) => {
            if (el !== this) el.pause();
          });
        });
      }

      disconnectedCallback() {
        if (!this.video) return;
        this.video.removeEventListener('play', this._onPlay);
        this.video.removeEventListener('pause', this._onPause);
        this.video.removeEventListener('ended', this._onEnded);
        this.video.removeEventListener('volumechange', this._onVolume);
      }

      // public API (Dawn-like: simple, attribute-driven, no framework)
      play() { return this.video?.play() ?? Promise.resolve(); }
      pause() { if (this.video && !this.video.paused) this.video.pause(); }
      togglePlay() { this.video?.paused ? this.play() : this.pause(); }
      toggleMute() { if (this.video) this.video.muted = !this.video.muted; }

      // private UI setters
      #setPlayUI(isPlaying) {
        if (!this.playButton) return;
        this.playButton.innerHTML = isPlaying ? ICONS.pause : ICONS.play;
        this.playButton.setAttribute('aria-pressed', String(isPlaying));
        this.playButton.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
      }
      #setMuteUI(isMuted) {
        if (!this.muteButton) return;
        this.muteButton.innerHTML = isMuted ? ICONS.muted : ICONS.volume;
        this.muteButton.setAttribute('aria-pressed', String(isMuted));
        this.muteButton.setAttribute('aria-label', isMuted ? 'Unmute' : 'Mute');
      }
    }
    customElements.define('hosted-video', HostedVideo);

    // ---- Delegated opener (replaces inline onclick) ----
    document.addEventListener('click', (evt) => {
      const btn = evt.target.closest('.js-video-open[data-video-target]');
      if (!btn) return;

      const id = btn.getAttribute('data-video-target');
      const node = id && document.getElementById(id);
      if (!node) return;

      const tryPlay = () => {
        // support id of <hosted-video>, <video>, or a wrapper containing a <video>
        if (node.tagName === 'HOSTED-VIDEO' && typeof node.play === 'function') return node.play().catch(() => { });
        if (node.tagName === 'VIDEO') return node.play().catch(() => { });
        const video = node.querySelector('video');
        return video?.play().catch(() => { });
      };

      tryPlay();                               // use the click gesture immediately
      requestAnimationFrame(() => setTimeout(tryPlay, 10)); // after modal opens
    });

    // Optional: pause videos when a native <dialog> closes
    document.addEventListener('close', (e) => {
      const dlg = e.target;
      if (!(dlg instanceof HTMLDialogElement)) return;
      dlg.querySelectorAll('hosted-video').forEach(el => el.pause());
    }, true);
  })()
})();